import { gql } from '@apollo/client';

import { CAREER_PROFILE_FIELDS } from '../fragments/careerProfile';

/**
 * Career profile writes (spec 0018, increment B).
 *
 * Operation names are deliberately distinct from the backend field names
 * (`SaveCareerProfileForm` vs `saveCareerProfile`, `SaveCvLocale` vs `saveCvText`)
 * — equal names collapse the generated result type (graphql-codegen skill).
 *
 * `saveCareerProfile` has delete-and-recreate semantics for most collections: the
 * client always sends the FULL collections (skills / projects / experience /
 * education / languages), never a delta. Certificates are the exception — they are
 * reconciled by `id` (see the CareerProfileFields fragment / transformProfileToInput).
 *
 * The mutation returns the FULL profile (fragment) so the caller can use the
 * response as the new form baseline without a refetch that would clobber edits
 * typed while the save was in flight.
 */
export const SaveCareerProfileFormMutation = gql`
  ${CAREER_PROFILE_FIELDS}
  mutation SaveCareerProfileForm(
    $input: CareerProfileInput!
    $photo: Upload
    $clearPhoto: Boolean
  ) {
    saveCareerProfile(input: $input, photo: $photo, clearPhoto: $clearPhoto) {
      success
      customErrors {
        field
        message
        code
      }
      profile {
        ...CareerProfileFields
      }
    }
  }
`;

/**
 * Per-language CV headline + about text (RUS / ENG / POL). Saved independently of
 * the language-neutral profile so switching the CV language never touches the
 * structured facts.
 */
export const SaveCvLocaleMutation = gql`
  mutation SaveCvLocale($language: String!, $jobTitle: String, $about: String) {
    saveCvText(language: $language, jobTitle: $jobTitle, about: $about) {
      success
      customErrors {
        field
        message
        code
      }
      profile {
        id
        completeness
      }
    }
  }
`;

/**
 * Partner-search opt-in (spec 0018 §3.1 — `is_searchable` defaults to false).
 * `contactsVisible` is a second, dependent consent.
 */
export const SetProfileSearchableMutation = gql`
  mutation SetProfileSearchable($isSearchable: Boolean!, $contactsVisible: Boolean) {
    setProfileSearchable(isSearchable: $isSearchable, contactsVisible: $contactsVisible) {
      success
      customErrors {
        field
        message
        code
      }
      profile {
        id
        isSearchable
        contactsVisible
      }
    }
  }
`;
